import { useState } from 'react'
import cx from 'classnames'
import { PanelError } from '@/console/api/errors'
import { BooleanWarehousePolicyType, WarehousePolicyType, WarehouseView } from '@/console/api/types'
import { toMasterGateError } from '@/console/api/warehousePolicy'
import { saveWarehousePolicies, SavePoliciesResult } from '@/console/data/warehousePolicy'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { Tag } from '@/console/ui/Tag'
import { PolicyCard } from './PolicyCard'
import { FlagEdits, flagRows, pendingFlagPolicies, POLICY_LABELS, unchosenFlagCount } from './policyForm'
import { FlagChoice, Switch } from './Switch'
import { WriteCallbacks } from './usePolicySave'
import styles from './Warehouse.module.css'

type Outcome = { savedCount: number; refetched: boolean; failed?: { type: WarehousePolicyType; error: PanelError } }

/**
 * LOGISPRD-15602 — 운영 플래그(JA_PICKUP · WCS_SORTER · B2B_CUTOFF · CAPACITY_MANAGED). 한 카드의 행 4개.
 *
 * 편집값은 손댄 행만 `edits` 에 담고, 손대지 않은 행은 서버 값을 그대로 보인다. 그래서 다른 카드처럼 서버 값 직렬화를
 * `key` 로 써서 리마운트하지 않는다 — 일부 저장 성공 후 재조회로 서버 값이 바뀌어도 실패·미전송 행의 편집값과
 * 부분 실패 문구가 남아야 하기 때문이다(LLD §4.4-1). 저장된 행은 편집값을 지워 새 서버 값을 따르게 한다.
 *
 * 서버에 없는 type(`미설정`) 행은 스위치 대신 ON/OFF 선택으로 보이고 처음엔 아무것도 고르지 않은 상태다. 기본 OFF 를 두면
 * 저장 한 번에 false 가 기록되기 때문이다. 미설정 행을 전부 고르기 전에는 저장을 막는다 — 고른 값을 저장해야 활성화 완비 조건이 채워진다.
 */
export const OperationFlagsCard = ({
  view,
  warehouseCode,
  onSavingChange,
  onSaved,
}: WriteCallbacks & { view: WarehouseView; warehouseCode: string }): JSX.Element => {
  const [edits, setEdits] = useState<FlagEdits>({})
  const [saving, setSaving] = useState(false)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const rows = flagRows(view, edits)
  const pending = pendingFlagPolicies(rows)
  // 미설정 행에서 값만 골라도(서버 값과 비교할 수 없어 changed 가 아니어도) 되돌릴 수 있어야 한다 — 편집 맵 기준으로 판정한다.
  const hasEdits = Object.keys(edits).length > 0
  const unsetCount = rows.filter((row) => !row.configured).length
  const unchosen = unchosenFlagCount(rows)

  const toggle = (type: BooleanWarehousePolicyType, enabled: boolean): void => {
    setEdits((prev) => ({ ...prev, [type]: enabled }))
    setOutcome(null)
  }

  const finish = (result: SavePoliciesResult): void => {
    if (result.saved.length > 0) {
      // 재조회가 성공했을 때만 편집값을 지운다 — 실패했는데 지우면 서버 값이 옛 값 그대로라 화면이 저장 전으로 되돌아가 보인다.
      // 편집값을 남기면 다시 저장해도 같은 값이라 멱등이다.
      if (result.refetched) {
        setEdits((prev) => {
          const next = { ...prev }
          result.saved.forEach((type) => {
            delete next[type as BooleanWarehousePolicyType]
          })
          return next
        })
      }
      onSaved()
    }
    setOutcome({
      savedCount: result.saved.length,
      refetched: result.refetched,
      failed: result.failed ? { type: result.failed.type, error: toMasterGateError(result.failed.error) } : undefined,
    })
  }

  const save = (): void => {
    if (pending.length === 0 || unchosen > 0) {
      return
    }
    setSaving(true)
    onSavingChange(true)
    setOutcome(null)
    // `saveWarehousePolicies` 는 throw 하지 않고 `{ saved, failed }` 로 돌려준다. 그래도 예상 밖 throw 는 첫 type 의 실패로 접는다.
    saveWarehousePolicies(warehouseCode, pending)
      .then(finish)
      .catch((error: unknown) => finish({ saved: [], refetched: false, failed: { type: pending[0].type, error } }))
      .finally(() => {
        setSaving(false)
        onSavingChange(false)
      })
  }

  const failedType = outcome?.failed?.type
  const staleNote = outcome && outcome.savedCount > 0 && !outcome.refetched ? `${outcome.savedCount}건 저장했지만 최신 값을 다시 읽지 못했어요. 새로고침하세요.` : null
  const unchosenNote = unchosen > 0 ? `미설정 ${unchosen}개의 값을 골라야 저장할 수 있어요.` : null
  const footerNote = staleNote ?? unchosenNote ?? (outcome && !outcome.failed ? `${outcome.savedCount}건 저장했어요.` : null)

  return (
    <PolicyCard
      title="운영 플래그"
      tags={unsetCount > 0 ? <Tag tone="warn">미설정 {unsetCount}</Tag> : null}
      flush
      error={
        outcome?.failed ? (
          <Callout tone="crit">
            <b className={styles.failLead}>
              {outcome.savedCount > 0 ? `${outcome.savedCount}건 저장, ` : ''}
              {POLICY_LABELS[outcome.failed.type]}({outcome.failed.type}) 에서 실패:
            </b>{' '}
            <span>{outcome.failed.error.message}</span>
          </Callout>
        ) : null
      }
      footerNote={footerNote}
      footer={
        <>
          <Button variant="line" size="sm" disabled={saving || !hasEdits} onClick={() => setEdits({})}>
            되돌리기
          </Button>
          <Button size="sm" disabled={saving || pending.length === 0 || unchosen > 0} onClick={save}>
            {saving ? '저장 중…' : `변경 ${pending.length}건 저장`}
          </Button>
        </>
      }
    >
      <ul className={styles.flagList}>
        {rows.map((row) => (
          <li key={row.type} className={cx(styles.flagRow, (row.pending || !row.configured) && styles.flagRowPending, failedType === row.type && styles.flagRowFailed)}>
            <div className={styles.flagMain}>
              <span className={styles.flagLabel}>{row.label}</span>
              <span className={styles.typeName}>{row.type}</span>
              {!row.configured ? <Tag tone="warn">미설정</Tag> : null}
            </div>
            <div className={styles.flagState}>
              {failedType === row.type ? <Tag tone="crit">실패</Tag> : null}
              {row.enabled === null ? <span className={styles.flagHint}>값을 선택하세요</span> : null}
              {row.changed ? <span className={styles.flagHint}>변경됨</span> : null}
              {row.configured ? (
                <Switch checked={row.enabled === true} label={row.label} disabled={saving} onChange={(enabled) => toggle(row.type, enabled)} />
              ) : (
                <FlagChoice value={row.enabled} label={row.label} disabled={saving} onChange={(enabled) => toggle(row.type, enabled)} />
              )}
            </div>
          </li>
        ))}
      </ul>
    </PolicyCard>
  )
}
